import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import strings from "global/constants/StringConstants";
import title from "global/constants/TitleConstants";
import urls from "global/constants/UrlConstants";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTitle } from "utils/UseTitle";
import Facebook from "./Component/Facebook";
import {
  getFacebookRedirectionURL,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import {
  authUserToFB,
  getIntegratedAppNames,
} from "./Component/FacebookService";
import metaIcon from "../../assets/icons1/meta.svg";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { hasAccessTo } from "layouts/Config";
import Unauthorized from "screens/Shared/Unauthorized";

const Integration = () => {
  useTitle(title.INTEGRATION);
  const dispatch = useDispatch();
  // const integrationAppName = useAppSelector(selectIntegrationAppName);
  const integrationAppName = "Facebook";
  const urlParams = useLocation().search;
  const code: any = new URLSearchParams(urlParams).get("code");
  const error: any = new URLSearchParams(urlParams).get("error");
  const [integrationApp, setIntegrationApp] = useState(strings.All);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFacebookIntegrated, setIsFacebookIntegrated] =
    useState<boolean>(false);
  const [isSalesforceIntegrated, setIsSalesforceIntegrated] =
    useState<boolean>(false);
  const [isWhatsAppIntegrated, setIsWhatsAppIntegrated] =
    useState<boolean>(false);
  const [isLinkedinIntegrated, setIsLinkedinIntegrated] =
    useState<boolean>(false);
  const [unexecutedCampaigns, setUnexecutedCampaigns] = useState<number>(0);

  const hasSocialFetchPermission = hasAccessTo(
    strings.social,
    strings.fetchPermission
  );

  useEffect(() => {
    appBasedUserAuthentication(code);
  }, [code]);

  useEffect(() => {
    if (isTruthy(error)) {
      const currentUrl = window.location.origin + window.location.pathname;
      window.location.replace(currentUrl);
    }
  }, [error]);

  useEffect(() => {
    areAppsIntegrated();
  }, []);

  const appBasedUserAuthentication = async (urlCode: any) => {
    if (isTruthy(urlCode)) {
      switch (integrationAppName) {
        case strings.FACEBOOK:
          await facebookAuthUser();
          break;
        case strings.WHATSAPP:
          !urls.PROD && (await whatsAppAuthUser());
          break;
        case strings.SALESFORCE:
          !urls.PROD && (await salesforceAuthUser());
          break;
      }
    }
    // dispatch(changeIntegrationAppNameAction(""));
  };

  const facebookAuthUser = async () => {
    try {
      setIsLoading(true);
      const response = await authUserToFB(
        code,
        getFacebookRedirectionURL(window.location.hostname),
        strings.STATE
      );
      openSuccessNotification(
        "Meta account is successfully integrated. Please go to social tab to manage the integrations."
      );
      setIsFacebookIntegrated(true);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const salesforceAuthUser = async () => {
    try {
      setIsLoading(true);
      // const response: any = await authUserToSalesforce(code);
      setIsSalesforceIntegrated(true);
      // openSuccessNotification(response.message);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setIsLoading(false);
    }
  };

  const areAppsIntegrated = async () => {
    try {
      setIsLoading(true);
      const isActiveArray = await getIntegratedAppNames();
      setIntegrationState(
        strings.FACEBOOK,
        isActiveArray,
        setIsFacebookIntegrated
      );
      !urls.PROD &&
        setIntegrationState(
          strings.WHATSAPP,
          isActiveArray,
          setIsWhatsAppIntegrated
        );
      !urls.PROD &&
        setIntegrationState(
          strings.SALESFORCE,
          isActiveArray,
          setIsSalesforceIntegrated
        );
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setIsLoading(false);
    }
  };

  const previousStepHandler = () => {
    setIntegrationApp(strings.All);
    // dispatch(changeIntegrationAppNameAction(""));
  };

  const setIntegrationState = (
    appName: string,
    activeAppArray: any[],
    setState: Function
  ) => {
    setState(activeAppArray.some((item: any) => item === appName));
  };

  const whatsAppAuthUser = async () => {
    try {
      setIsLoading(true);
      // await authUserToWhatsApp(
      //   code,
      //   getFacebookRedirectionURL(window.location.hostname),
      //   strings.STATE
      // );
      openSuccessNotification(
        "WhatsApp account is successfully integrated. Please go to social tab to manage the integrations."
      );
      setIsWhatsAppIntegrated(true);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setIsLoading(false);
    }
  };
  const getIntegrationAppsDisplayList = (
    integrationAppName: string,
    image: string,
    displayText: string,
    longText: string,
    integrated: any
  ) => {
    return (
      <Card
        sx={{
          height: "100%",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
        onClick={() => setIntegrationApp(integrationAppName)}
      >
        <Stack direction="column" spacing={3} p={3}>
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                component="img"
                src={image}
                height={35}
                width={35}
                mb={2}
                mt={1}
              />
              <Stack direction="column">
                <Typography variant="h6" color="text.primary">
                  {displayText}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {displayText.toLocaleLowerCase()}.com
                </Typography>
              </Stack>
            </Stack>
            {integrated && (
              <Box>
                <CheckCircleIcon fontSize="medium" htmlColor="#39ae41" />
              </Box>
            )}
          </Stack>
          <Typography variant="body2" color="text.primary">
            {longText}
          </Typography>
        </Stack>
        {integrated && (
          <Box>
            <Divider />
            <Box
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Button id="integrated" variant="contained" size="small">
                {integrated}
              </Button>
            </Box>
          </Box>
        )}
      </Card>
    );
  };

  const cardData = [
    {
      integrationAppName: strings.FACEBOOK,
      shouldDisplay: strings.isFacebookReleased,
      image: metaIcon,
      displayText: "Meta",
      longText:
        "Improve your customer service and streamline your social media management using Mailzzy's integration with Meta.",
      // "Upgrade your customer service game and simplify your social media management with Mailzzy's Facebook integration",
      // "Connect Facebook  to create your ad campaigns directly from Mailzzy, saving time and increasing efficiency.",

      integrated: isFacebookIntegrated && "View Integration",
    },
    // ...(!urls.PROD
    //   ? [
    //       {
    //         integrationAppName: strings.SALESFORCE,
    //         shouldDisplay: strings.isSalesforceReleased,
    //         image: salesforceIcon,
    //         displayText: "SalesForce",
    //         longText:
    //           "Integrate the SalesForce with Mailzzy to utilize the power of both.",
    //         integrated: isSalesforceIntegrated && "Integrated",
    //       },
    //       {
    //         integrationAppName: strings.WHATSAPP,
    //         shouldDisplay: strings.isWhatsappReleased,
    //         image: whatsAppIcon,
    //         displayText: "WhatsApp",
    //         longText:
    //           "Improve your customer service and streamline your social media management using Mailzzy's integration with WhatsApp.",
    //         integrated: isWhatsAppIntegrated && "Integrated",
    //       },
    //     ]
    //   : []),
    // {
    //   integrationAppName: strings.LINKEDIN,
    //   shouldDisplay: strings.isLinkedinReleased,
    //   //   image: linkedinIcon,
    //   displayText: "LinkdedIn",
    //   longText:
    //     "Make smarter marketing decisions when you sync your LinkedIn Lead Gen Forms with Mailzzy.",
    //   integrated: isLinkedinIntegrated && "Integrated",
    // },
  ];

  const socialCardHandler = () => {
    switch (integrationApp) {
      case strings.FACEBOOK:
        return (
          <Facebook
            previousStepHandler={previousStepHandler}
            isFacebookIntegrated={isFacebookIntegrated}
            setIsFacebookIntegrated={setIsFacebookIntegrated}
            isLoggedInSocialMedia={areAppsIntegrated}
            unexecutedCampaigns={unexecutedCampaigns}
            setUnexecutedCampaigns={setUnexecutedCampaigns}
          />
        );
      case strings.All:
        return cardData
          .filter((cardItem: any) => cardItem.shouldDisplay)
          .map((cardItem: any) => {
            return (
              <Grid item lg={3} pl={"0px !important"} pr={2}>
                {getIntegrationAppsDisplayList(
                  cardItem.integrationAppName,
                  cardItem.image,
                  cardItem.displayText,
                  cardItem.longText,
                  cardItem.integrated
                )}
              </Grid>
            );
          });
      default:
        break;
    }
  };

  const getIntegrationPage = () => {
    return (
      <Stack direction="column" spacing={1}>
        <Typography variant="h5" color="text.primary">
          Integrations
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Say goodbye to manual data transfer and hello to seamless
          integrations!
        </Typography>
      </Stack>
    );
  };

  const getStatsSkeleton = () => (
    <Grid container spacing={2}>
      {cardData.map((_, index) => (
        <Grid
          item
          lg={3}
          md={3}
          xl={3}
          sm={12}
          xs={12}
          pl={"0px !important"}
          pr={2}
        >
          <Skeleton animation="wave" variant="rounded" height={235} />
        </Grid>
      ))}
    </Grid>
  );

  return hasSocialFetchPermission ? (
    <CenteredAuthenticatedLayout>
      {getIntegrationPage()}
      <Stack direction="column" spacing={2} mt={3}>
        {isLoading ? (
          getStatsSkeleton()
        ) : (
          <Grid container spacing={1}>
            {socialCardHandler()}
          </Grid>
        )}
      </Stack>
    </CenteredAuthenticatedLayout>
  ) : (
    <Unauthorized permissionDenied={hasSocialFetchPermission} />
  );
};
export default Integration;
